import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, Clock, Calendar, MapPin, Ticket, ArrowLeft, 
  ChevronRight, ExternalLink, Play, X
} from 'lucide-react';
import { getShowById, getTourDates } from '../services/cmsData';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.mt-productions.co.uk';

// Helper to group dates by venue and date (combining multiple times)
const groupDatesByVenueAndDay = (dates) => {
  const grouped = {};
  dates.forEach(date => {
    const key = `${date.venue}-${date.date}`;
    if (!grouped[key]) {
      grouped[key] = {
        ...date,
        times: [{ time: date.time, ticketUrl: date.ticketUrl, status: date.status, id: date.id }]
      };
    } else {
      grouped[key].times.push({ time: date.time, ticketUrl: date.ticketUrl, status: date.status, id: date.id });
    }
  });
  // Sort times within each group
  Object.values(grouped).forEach(g => {
    g.times.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  });
  return Object.values(grouped);
};

// Video Player Component - Glossy embedded player
const VideoPlayer = ({ videoUrl, poster }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Extract video ID and determine platform
  const getEmbedUrl = (url) => {
    if (!url) return null;
    
    // YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&\s?]+)/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1&rel=0`;
    }
    
    // Vimeo
    const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    }
    
    return null;
  };

  const embedUrl = getEmbedUrl(videoUrl);
  const isDirectVideo = videoUrl && /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(videoUrl);
  
  if (!videoUrl) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative rounded-2xl overflow-hidden shadow-2xl"
      style={{
        background: 'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.1)'
      }}
    >
      {/* Glossy border effect */}
      <div className="absolute inset-0 rounded-2xl border border-white/20 pointer-events-none z-10" />
      
      {/* Video container */}
      <div className="relative aspect-video bg-black/50">
        {isPlaying && isDirectVideo ? (
          <video
            src={videoUrl}
            className="absolute inset-0 w-full h-full"
            controls
            autoPlay
            playsInline
            data-testid="direct-video-player"
          />
        ) : isPlaying && embedUrl ? (
          <iframe
            src={embedUrl}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Show Promo Video"
          />
        ) : (
          <>
            {/* Poster/Thumbnail */}
            <img
              src={poster}
              alt="Video thumbnail"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50" />
            
            {/* Large Centered Play Button */}
            <button
              onClick={() => setIsPlaying(true)}
              data-testid="video-play-button"
              className="absolute inset-0 flex flex-col items-center justify-center group cursor-pointer"
            >
              {/* Play Icon Circle */}
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="relative"
              >
                {/* Outer ring pulse animation */}
                <div className="absolute inset-0 w-28 h-28 rounded-full bg-white/30 animate-ping" style={{ animationDuration: '2s' }} />
                
                {/* Main play button */}
                <div className="relative w-28 h-28 rounded-full bg-white flex items-center justify-center shadow-2xl shadow-white/40 transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-white/60">
                  <Play className="w-12 h-12 text-brand-dark ml-2" fill="currentColor" />
                </div>
              </motion.div>
              
              {/* Watch Trailer Text - Below Play Button */}
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mt-6 text-center"
              >
                <span className="text-white font-bold text-lg tracking-wide uppercase">
                  Watch Trailer
                </span>
                <div className="text-white/60 text-sm mt-1">
                  Click to play
                </div>
              </motion.div>
            </button>
          </>
        )}
      </div>
    </motion.div>
  );
};

// Gallery Component - Amazing masonry-style with lightbox
const Gallery = ({ images, showTitle }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});

  const nextImage = () => {
    if (!images || images.length === 0) return;
    setActiveIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    if (!images || images.length === 0) return;
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Handle keyboard navigation - must be called unconditionally
  useEffect(() => {
    if (!lightboxOpen || !images || images.length === 0) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, images]);

  const handleImageLoad = (index) => {
    setLoadedImages(prev => ({ ...prev, [index]: true }));
  };

  // Early return after all hooks
  if (!images || images.length === 0) return null;

  return (
    <section className="py-20 bg-gradient-to-b from-brand-dark to-gray-900 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="font-accent text-accent-gold text-sm tracking-[0.3em] mb-3 uppercase">
            Behind The Scenes
          </p>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-white">
            Production Gallery
          </h2>
        </motion.div>
        
        {/* Masonry-style Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
          {images.map((image, index) => {
            // Make first image and every 5th image larger
            const isLarge = index === 0 || index === 5;
            const isLoaded = loadedImages[index];
            
            return (
              <motion.button
                key={index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => {
                  setActiveIndex(index);
                  setLightboxOpen(true);
                }}
                className={`relative overflow-hidden rounded-2xl group ${
                  isLarge ? 'col-span-2 row-span-2' : ''
                }`}
                style={{ aspectRatio: isLarge ? '1' : '1' }}
              >
                {/* Skeleton placeholder */}
                {!isLoaded && (
                  <div className="absolute inset-0 bg-gray-700 animate-pulse">
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-700 via-gray-600 to-gray-700 animate-shimmer" 
                         style={{ backgroundSize: '200% 100%' }} />
                  </div>
                )}
                
                {/* Image */}
                <img
                  src={image}
                  alt={`${showTitle} - Image ${index + 1}`}
                  className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-110 ${
                    isLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  loading="lazy"
                  onLoad={() => handleImageLoad(index)}
                />
                
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Hover Effect - Glossy Border */}
                <div className="absolute inset-0 rounded-2xl border-2 border-white/0 group-hover:border-white/30 transition-all duration-500" />
                
                {/* View Icon */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center transform scale-50 group-hover:scale-100 transition-transform duration-500">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
                
                {/* Image Number */}
                <div className="absolute bottom-3 left-3 px-2 py-1 rounded-md bg-black/50 backdrop-blur-sm text-white/70 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  {index + 1} / {images.length}
                </div>
              </motion.button>
            );
          })}
        </div>
        
        {/* Photo Credit */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-white/40 text-sm mt-8"
        >
          Photography by Pamela Raith
        </motion.p>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/98 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          {/* Close Button */}
          <button 
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all duration-300 z-10"
            onClick={() => setLightboxOpen(false)}
          >
            <X className="w-6 h-6" />
          </button>
          
          {/* Navigation Arrows */}
          <button 
            className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all duration-300 z-10"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <button 
            className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all duration-300 z-10"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          
          {/* Main Image */}
          <motion.div
            key={activeIndex}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="relative max-w-[90vw] max-h-[85vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[activeIndex]}
              alt={`${showTitle} - Image ${activeIndex + 1}`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            
            {/* Image Counter */}
            <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md text-white text-sm font-medium">
              {activeIndex + 1} / {images.length}
            </div>
          </motion.div>
          
          {/* Thumbnail Strip */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 px-4 py-3 rounded-2xl bg-white/5 backdrop-blur-md max-w-[90vw] overflow-x-auto">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={(e) => { e.stopPropagation(); setActiveIndex(index); }}
                className={`w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 transition-all duration-300 ${
                  index === activeIndex 
                    ? 'ring-2 ring-accent-gold ring-offset-2 ring-offset-black/50 scale-110' 
                    : 'opacity-50 hover:opacity-100'
                }`}
              >
                <img
                  src={image}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </motion.div>
      )}
    </section>
  );
};

export default function ShowDetailPage() {
  const { showId } = useParams();
  const location = useLocation();
  const [show, setShow] = useState(null);
  const [tourDates, setTourDates] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [showData, datesData] = await Promise.all([
          getShowById(showId),
          getTourDates({ showId })
        ]);
        setShow(showData);
        // Group dates by venue and day to combine multiple times
        setTourDates(groupDatesByVenueAndDay(datesData));

        // Fetch gallery images from R2
        try {
          const res = await fetch(`${API_URL}/api/gallery/${showId}`);
          if (res.ok) {
            const data = await res.json();
            if (data.images && data.images.length > 0) {
              setGalleryImages(data.images);
            }
          }
        } catch (err) {
          console.error('Gallery fetch error:', err);
        }
      } catch (error) {
        console.error('Error fetching show:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [showId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark pt-24">
        <div className="container-wide px-4">
          <div className="skeleton h-96 rounded-xl mb-8" />
          <div className="skeleton h-8 w-64 rounded mb-4" />
          <div className="skeleton h-4 w-full max-w-2xl rounded mb-2" />
          <div className="skeleton h-4 w-full max-w-xl rounded" />
        </div>
      </div>
    );
  }

  if (!show) {
    return (
      <div className="min-h-screen bg-brand-dark pt-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-3xl text-white mb-4">Show Not Found</h1>
          <Link to="/shows" className="btn-primary">
            View All Shows
          </Link>
        </div>
      </div>
    );
  }

  // Coming Soon shows get a minimal page (poster + "More information coming soon")
  if (show.status === 'coming-soon') {
    return (
      <div data-testid="show-detail-page" className="min-h-screen bg-brand-dark pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <Link
            to="/"
            data-testid="back-to-home"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-10 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid md:grid-cols-[1fr_1.2fr] gap-12 items-center"
          >
            <div className="aspect-[3/4] overflow-hidden shadow-2xl">
              <img
                src={`${process.env.PUBLIC_URL}${show.poster}`}
                alt={show.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-center md:text-left">
              <p className="font-accent text-accent-gold text-sm tracking-[0.3em] mb-4 uppercase">
                Coming Soon
              </p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {show.title}
              </h1>
              <p className="font-body text-white/70 text-lg lg:text-xl leading-relaxed">
                More information coming soon.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Use banner if available, otherwise fall back to hero/poster
  const heroImage = `${process.env.PUBLIC_URL}${show.banner || show.hero || show.poster}`;

  return (
    <div data-testid="show-detail-page" className="min-h-screen bg-brand-dark">
      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[400px] lg:h-[60vh]">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt={show.title}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/50 to-brand-dark/20" />
        </div>

        <div className="absolute inset-0 flex items-end">
          <div className="max-w-6xl mx-auto px-6 pb-12 w-full">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Link
                to="/shows"
                data-testid="back-to-shows"
                className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to All Shows
              </Link>

              <div className="flex items-start gap-2 mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  show.status === 'current'
                    ? 'bg-green-500 text-white'
                    : show.status === 'available'
                    ? 'bg-blue-500 text-white'
                    : show.status === 'pantomime'
                    ? 'bg-purple-500 text-white'
                    : 'bg-neutral-grey/50 text-white'
                }`}>
                  {show.status === 'current' ? 'Now Touring' : show.status === 'available' ? 'Available to Book' : show.status === 'pantomime' ? 'Pantomime' : 'Past Production'}
                </span>
              </div>

              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3">
                {show.title}
              </h1>
              
              <p className="font-display text-lg lg:text-xl text-accent-gold mb-6">
                {show.tagline}
              </p>

              <div className="flex flex-wrap items-center gap-6 text-white/80">
                <span className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-accent-gold" />
                  Ages {show.ageRange}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-accent-gold" />
                  {show.duration}
                </span>
                {show.interval && (
                  <span className="text-sm text-white/60">
                    Includes interval
                  </span>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Video Player - Above About */}
              {show.videoUrl && (
                <VideoPlayer videoUrl={show.videoUrl} poster={`${process.env.PUBLIC_URL}${show.poster}`} />
              )}

              {/* About */}
              <div>
                <h2 className="font-display text-2xl font-bold text-white mb-6">
                  About the Show
                </h2>
                <p className="font-body text-white/70 text-lg leading-relaxed">
                  {show.description}
                </p>
              </div>
            </div>

            {/* Sidebar - Tour Dates */}
            <div>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 sticky top-24">
                <h3 className="font-display text-xl font-bold text-white mb-2">
                  Tour Dates
                </h3>
                <p className="text-white/60 text-sm mb-6">
                  {tourDates.length} upcoming {tourDates.length === 1 ? 'date' : 'dates'}
                </p>

                {tourDates.length > 0 ? (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                    {tourDates.slice(0, 10).map((date, index) => (
                      <motion.div
                        key={`${date.venue}-${date.date}-${index}`}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-colors"
                      >
                        <div className="flex flex-col gap-2">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-white text-sm truncate">
                                {date.town}
                              </div>
                              <div className="text-white/60 text-xs truncate">
                                {date.venue}
                              </div>
                              <div className="flex items-center gap-2 mt-2 text-xs text-white/50">
                                <Calendar className="w-3 h-3" />
                                {new Date(date.date).toLocaleDateString('en-GB', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </div>
                            </div>
                          </div>
                          {/* Show all times with ticket links */}
                          <div className="flex flex-wrap gap-2 mt-1">
                            {date.times ? date.times.map((t) => (
                              <a
                                key={t.id}
                                href={t.ticketUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-accent-gold/20 hover:bg-accent-gold text-accent-gold hover:text-brand-dark rounded-full text-xs font-medium transition-colors"
                              >
                                <Ticket className="w-3 h-3" />
                                {t.time}
                              </a>
                            )) : (
                              <a
                                href={date.ticketUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-accent-gold/20 hover:bg-accent-gold text-accent-gold hover:text-brand-dark rounded-full text-xs font-medium transition-colors"
                              >
                                <Ticket className="w-3 h-3" />
                                {date.time}
                              </a>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/50 text-sm">
                    No upcoming tour dates announced yet.
                  </p>
                )}

                {tourDates.length > 10 && (
                  <Link
                    to={`/tour-dates?showId=${show.id}`}
                    className="mt-4 inline-flex items-center gap-2 text-accent-gold hover:text-white text-sm font-medium transition-colors"
                  >
                    View all {tourDates.length} dates
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                )}

                <Link
                  to={`/tour-dates?showId=${show.id}`}
                  data-testid="find-tickets-btn"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105"
                  style={{ backgroundColor: '#D4AF37', color: '#020120' }}
                >
                  <Ticket className="w-5 h-5" />
                  Find Tickets
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section - R2 images first, local fallback */}
      {galleryImages.length > 0 ? (
        <Gallery images={galleryImages} showTitle={show.title} />
      ) : show.gallery && show.gallery.length > 0 ? (
        <Gallery images={show.gallery.map(img => `${process.env.PUBLIC_URL}${img}`)} showTitle={show.title} />
      ) : null}

      {/* CTA Section */}
      <section className="py-16 bg-brand-primary/20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl font-bold text-white mb-4">
            Don't Miss Out!
          </h2>
          <p className="text-white/70 mb-8">
            Book your tickets now for an unforgettable family experience.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to={`/tour-dates?showId=${show.id}`}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold transition-all duration-300 hover:scale-105"
              style={{ backgroundColor: '#D4AF37', color: '#020120' }}
            >
              <Ticket className="w-5 h-5" />
              Book Tickets
            </Link>
            <Link
              to="/shows"
              className="inline-flex items-center gap-2 border border-white/20 text-white px-8 py-4 rounded-full font-semibold transition-all duration-500 hover:bg-white/10"
            >
              View More Shows
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
